import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { formatCad, type InvoiceTotals, type TaxReport } from "@shared/modules/x-atelier";
import { BackLink } from "../../../core/components/ui/back-link";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Toggle } from "../../../core/components/ui/toggle";
import { getTaxReport, taxReportCsvUrl } from "../lib/api";

function monthLabel(key: string): string {
  const [y, m] = key.split("-").map(Number);
  const label = new Date(y!, (m ?? 1) - 1, 1).toLocaleDateString("fr-CA", {
    month: "long",
    year: "numeric",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function quarterLabel(q: number, year: number): string {
  return `${q === 1 ? "1er" : `${q}e`} trimestre ${year}`;
}

const EMPTY: InvoiceTotals = {
  count: 0,
  subtotalCents: 0,
  gstCents: 0,
  qstCents: 0,
  importedTaxCents: 0,
  totalCents: 0,
  paidCents: 0,
  dueCents: 0,
};

function add(a: InvoiceTotals, b: InvoiceTotals): InvoiceTotals {
  return {
    count: a.count + b.count,
    subtotalCents: a.subtotalCents + b.subtotalCents,
    gstCents: a.gstCents + b.gstCents,
    qstCents: a.qstCents + b.qstCents,
    importedTaxCents: a.importedTaxCents + b.importedTaxCents,
    totalCents: a.totalCents + b.totalCents,
    paidCents: a.paidCents + b.paidCents,
    dueCents: a.dueCents + b.dueCents,
  };
}

/**
 * Taxes à remettre : TPS et TVQ facturées, mois par mois ou par trimestre,
 * pour une année — ce que QuickBooks appelle le rapport de taxes de vente.
 * Calcul sur la date d'émission (comptabilité d'exercice), annulées exclues.
 */
export function TaxesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const year = Number(searchParams.get("annee")) || new Date().getFullYear();
  const [report, setReport] = useState<TaxReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [byQuarter, setByQuarter] = useState(false);

  const from = useMemo(() => new Date(year, 0, 1).toISOString(), [year]);
  const to = useMemo(() => new Date(year + 1, 0, 1).toISOString(), [year]);

  useEffect(() => {
    let cancelled = false;
    setReport(null);
    getTaxReport(from, to)
      .then((r) => {
        if (cancelled) return;
        setReport(r);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const rows = useMemo(() => {
    if (!report) return [];
    if (!byQuarter)
      return report.months.map((m) => ({ key: m.month, label: monthLabel(m.month), totals: m }));
    const quarters = new Map<number, InvoiceTotals>();
    for (const m of report.months) {
      const q = Math.floor((Number(m.month.slice(5, 7)) - 1) / 3) + 1;
      quarters.set(q, add(quarters.get(q) ?? EMPTY, m));
    }
    return [...quarters.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([q, totals]) => ({ key: `T${q}`, label: quarterLabel(q, year), totals }));
  }, [report, byQuarter, year]);

  const hasImported = Boolean(report && report.totals.importedTaxCents > 0);
  const setYear = (y: number) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("annee", String(y));
        return next;
      },
      { replace: true },
    );

  return (
    <div className="page taxes">
      <p className="atelier-backlink">
        <BackLink fallback="/factures" />
      </p>
      <PageHeader
        title="Taxes à remettre"
        subtitle="TPS et TVQ facturées, d'après la date d'émission des factures."
        actions={
          <Button
            variant="ghost"
            icon={<Icon name="file" size={18} />}
            onClick={() => window.open(taxReportCsvUrl(from, to), "_blank")}
          >
            Exporter CSV
          </Button>
        }
      />

      <div className="atelier-toolbar">
        <span className="factures__nav">
          <Button
            variant="ghost"
            size="sm"
            aria-label="Année précédente"
            onClick={() => setYear(year - 1)}
          >
            <Icon name="chevron-left" size={18} />
          </Button>
          <span className="factures__nav-label">{year}</span>
          <Button
            variant="ghost"
            size="sm"
            aria-label="Année suivante"
            onClick={() => setYear(year + 1)}
          >
            <Icon name="chevron-right" size={18} />
          </Button>
        </span>
        <div className="atelier-toolbar__spacer" />
        <Toggle checked={byQuarter} onChange={setByQuarter} label="Par trimestre" />
      </div>

      {failed ? (
        <p className="empty">Rapport indisponible — réessayez dans un instant.</p>
      ) : !report ? (
        <p className="empty">Chargement…</p>
      ) : (
        <>
          <div className="factures__stats taxes__stats">
            <div className="atelier-stat">
              <span className="atelier-stat__label">Ventes {year} (hors taxes)</span>
              <span className="atelier-stat__value">{formatCad(report.totals.subtotalCents)}</span>
              <span className="atelier-stat__note">
                {report.totals.count} facture{report.totals.count > 1 ? "s" : ""}
              </span>
            </div>
            <div className="atelier-stat atelier-stat--accent">
              <span className="atelier-stat__label">TPS à remettre</span>
              <span className="atelier-stat__value">{formatCad(report.totals.gstCents)}</span>
              <span className="atelier-stat__note">5 % du sous-total</span>
            </div>
            <div className="atelier-stat atelier-stat--accent">
              <span className="atelier-stat__label">TVQ à remettre</span>
              <span className="atelier-stat__value">{formatCad(report.totals.qstCents)}</span>
              <span className="atelier-stat__note">9,975 % du sous-total</span>
            </div>
            <div className="atelier-stat">
              <span className="atelier-stat__label">Total facturé (TTC)</span>
              <span className="atelier-stat__value">{formatCad(report.totals.totalCents)}</span>
              <span className="atelier-stat__note">
                {hasImported
                  ? `dont ${formatCad(report.totals.importedTaxCents)} de taxes Urable`
                  : " "}
              </span>
            </div>
          </div>

          <div className="card taxes__table-card">
            <table className="table taxes__table">
              <thead>
                <tr>
                  <th>{byQuarter ? "Trimestre" : "Mois"}</th>
                  <th className="travail__num">Factures</th>
                  <th className="travail__num">Sous-total</th>
                  <th className="travail__num">TPS</th>
                  <th className="travail__num">TVQ</th>
                  {hasImported ? <th className="travail__num">Taxes Urable</th> : null}
                  <th className="travail__num">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.key}
                    className={r.totals.count === 0 ? "taxes__row--empty" : undefined}
                  >
                    <td className="taxes__label">{r.label}</td>
                    <td className="travail__num">{r.totals.count}</td>
                    <td className="travail__num">{formatCad(r.totals.subtotalCents)}</td>
                    <td className="travail__num">{formatCad(r.totals.gstCents)}</td>
                    <td className="travail__num">{formatCad(r.totals.qstCents)}</td>
                    {hasImported ? (
                      <td className="travail__num">{formatCad(r.totals.importedTaxCents)}</td>
                    ) : null}
                    <td className="travail__num">{formatCad(r.totals.totalCents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="taxes__total">
                  <td>Total {year}</td>
                  <td className="travail__num">{report.totals.count}</td>
                  <td className="travail__num">{formatCad(report.totals.subtotalCents)}</td>
                  <td className="travail__num">{formatCad(report.totals.gstCents)}</td>
                  <td className="travail__num">{formatCad(report.totals.qstCents)}</td>
                  {hasImported ? (
                    <td className="travail__num">{formatCad(report.totals.importedTaxCents)}</td>
                  ) : null}
                  <td className="travail__num">{formatCad(report.totals.totalCents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="taxes__note">
            Montants calculés sur la date d'émission des factures (méthode de comptabilité
            d'exercice), factures annulées exclues. Les taxes des factures reprises d'Urable ne sont
            pas ventilées entre TPS et TVQ : elles apparaissent à part. Les crédits de taxes sur
            intrants (achats) ne sont pas suivis ici.
          </p>
        </>
      )}
    </div>
  );
}
